import { z } from 'zod';
import { moneySchema } from './common.schemas';
import { defineListQuery, inForceFilter, listFilter } from './list-query.schemas';

/**
 * Accessorial charge types and their per-customer rates (PRD → Rate cards →
 * Accessorials; gitaumoses4/chukua#26): priced once per customer and applied
 * per job as it occurs. Rates are versioned the same way rate lines are —
 * see `rate-card.schemas.ts`.
 */

const codeSchema = z.string().trim().min(1).max(32);
const nameSchema = z.string().trim().min(1).max(120);

export const ACCESSORIAL_UNITS = ['per_hour', 'per_day', 'per_event'] as const;
export type AccessorialUnit = (typeof ACCESSORIAL_UNITS)[number];

/** `POST /accessorial-types`, `PATCH /accessorial-types/:id` — the catalogue (PRD → Stays configurable → Pricing). */
export const accessorialTypeInputSchema = z.object({
  code: codeSchema,
  name: nameSchema,
  unit: z.enum(ACCESSORIAL_UNITS),
  /** Whether this type carries free hours/days before it charges (waiting, detention) — see `accessorial-rate-free-quantity.policy.ts`. */
  hasFreeQuantity: z.boolean(),
});
export type AccessorialTypeInput = z.infer<typeof accessorialTypeInputSchema>;

/** `POST /rate-cards/:id/accessorial-rates` — the type's own `hasFreeQuantity` decides whether `freeQuantity` is required; checked server-side against the catalogue, not here. */
export const accessorialRateInputSchema = z
  .object({
    accessorialTypeCode: codeSchema,
    amount: moneySchema.refine((money) => money.amountMinor >= 0, { message: 'Amount cannot be negative', path: ['amountMinor'] }),
    freeQuantity: z.number().int().min(0).nullable().default(null),
    validFrom: z.iso.date(),
    validTo: z.iso.date().optional(),
  })
  .refine((input) => input.validTo === undefined || input.validTo >= input.validFrom, { message: 'validTo cannot be before validFrom', path: ['validTo'] });
export type AccessorialRateInput = z.infer<typeof accessorialRateInputSchema>;

/** `POST /accessorial-rates/:id/supersede` — the key (card, type) carries over. */
export const supersedeAccessorialRateInputSchema = z.object({
  amount: moneySchema.refine((money) => money.amountMinor >= 0, { message: 'Amount cannot be negative', path: ['amountMinor'] }),
  freeQuantity: z.number().int().min(0).nullable().default(null),
  effectiveDate: z.iso.date(),
});
export type SupersedeAccessorialRateInput = z.infer<typeof supersedeAccessorialRateInputSchema>;

/** `POST /accessorial-rates/:id/end` — last day the rate applies, inclusive. */
export const endAccessorialRateInputSchema = z.object({ validTo: z.iso.date() });
export type EndAccessorialRateInput = z.infer<typeof endAccessorialRateInputSchema>;

export const accessorialRateListQuerySchema = defineListQuery({
  sortFields: ['validFrom', 'amountMinor', 'createdAt'],
  defaultSort: { sort: 'validFrom', order: 'desc' },
  filters: { asOf: z.iso.date().optional(), current: inForceFilter, accessorialTypeCode: listFilter(codeSchema) },
});
export type AccessorialRateListQuery = z.infer<typeof accessorialRateListQuerySchema>;

/** `GET /accessorial-rate-lookup` — the type plus the job's date. No `customerId` looks on the standard card only. */
export const accessorialRateLookupQuerySchema = z.object({
  customerId: z.string().uuid().optional(),
  accessorialTypeCode: codeSchema,
  jobDate: z.iso.date(),
});
export type AccessorialRateLookupQuery = z.infer<typeof accessorialRateLookupQuerySchema>;

/** `POST /jobs/:id/accessorial-terms/:accessorialTypeCode/override` — correct the free hours/days copied onto a job (gitaumoses4/chukua#33); the reason is audited, not stored on the row. */
export const jobAccessorialOverrideInputSchema = z.object({
  freeQuantity: z.number().int().min(0),
  reason: z.string().trim().min(1).max(500),
});
export type JobAccessorialOverrideInput = z.infer<typeof jobAccessorialOverrideInputSchema>;
